import { Fragment, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import { navFor, NavContext } from './navConfig';
import Avatar from './Avatar';
import { LEGAL_LINKS, COPYRIGHT } from './legal';
import type { ShellUser } from './Header';
import type { ReactNode } from 'react';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  brandName: string;
  logo: ReactNode;
  user: ShellUser;
  navCtx: NavContext;
  onSignOut: () => void;
}

/**
 * Mobile navigation drawer (PRD §6.4.3): 320px / max 85vw, slides in from the
 * left over a backdrop. Profile header, scrollable nav list, Sign Out
 * separated at the bottom. Backdrop click and Escape close it.
 */
export default function MobileDrawer({ open, onClose, brandName, logo, user, navCtx, onSignOut }: MobileDrawerProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const items = navFor('drawer', navCtx);

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden />
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Navigation menu">
        <div className="drawerhead">
          <Avatar user={user} />
          <div className="drawerid">
            <div className="drawerbrand">{brandName}</div>
            <div className="drawername">{user.name}</div>
            {user.email && <div className="draweremail">{user.email}</div>}
          </div>
          <button ref={closeRef} className="iconbtn" aria-label="Close menu" onClick={onClose}>
            <X size={22} aria-hidden />
          </button>
        </div>

        <nav className="drawernav" aria-label="Menu">
          {items.map(({ key, label, to, Icon }) => (
            <NavLink key={key} to={to} className={({ isActive }) => `nav${isActive ? ' active' : ''}`} onClick={onClose}>
              <Icon size={20} aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="drawerfoot">
          <button className="nav" onClick={onSignOut}>
            <LogOut size={20} aria-hidden />
            Sign Out
          </button>
          <div className="drawerlegal">
            {LEGAL_LINKS.map(({ label, to }, i) => (
              <Fragment key={label}>
                {i > 0 && <span aria-hidden>·</span>}
                <Link to={to} onClick={onClose}>{label}</Link>
              </Fragment>
            ))}
          </div>
          <div className="drawercopy">{COPYRIGHT}</div>
        </div>
      </aside>
    </>
  );
}
